import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Trash2,
  RefreshCw,
  CheckCircle,
  Eye,
  Mail,
  Phone,
  Briefcase,
  Send,
  X
} from 'lucide-react';
import { getAdminToken, getBackendUrl } from '../utils/auth';

const API_URL = getBackendUrl();

const AdminContactEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  // Reply Modal State
  const [replyModal, setReplyModal] = useState({
    isOpen: false,
    enquiryId: null,
    name: '',
    email: '',
    subject: 'Re: Your Enquiry with CLIENTCODE AI',
    message: ''
  });
  const [isSendingReply, setIsSendingReply] = useState(false);

  const fetchEnquiries = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        headers: {
          'Authorization': `Bearer ${getAdminToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setEnquiries(data.contacts || []);
      } else {
        setError(data.message || 'Failed to load enquiries');
      }
    } catch (err) {
      console.error('Error fetching enquiries:', err);
      setError('Error loading data from server');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleStatusUpdate = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/api/contact/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        fetchEnquiries(true);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handleOpenReplyModal = (enquiry) => {
    setReplyModal({
      isOpen: true,
      enquiryId: enquiry._id,
      name: enquiry.name,
      email: enquiry.email,
      subject: `Re: Your Enquiry regarding ${enquiry.service}`,
      message: `Dear ${enquiry.name},\n\nThank you for reaching out to CLIENTCODE AI regarding ${enquiry.service}. We have received your enquiry and our team is currently reviewing your requirements.\n\nWe will get back to you shortly with more details.\n\nPlease feel free to reply to this email if you have any immediate questions.`
    });
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    setIsSendingReply(true);
    try {
      const response = await fetch(`${API_URL}/api/contact/${replyModal.enquiryId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify({
          subject: replyModal.subject,
          message: replyModal.message
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        alert('Reply email sent successfully!');
        setReplyModal(prev => ({ ...prev, isOpen: false }));
        fetchEnquiries(true);
      } else {
        throw new Error(data.message || 'Failed to send reply');
      }
    } catch (err) {
      console.error('Error sending reply:', err);
      alert(err.message || 'Error sending email');
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this enquiry?")) return;
    try {
      const response = await fetch(`${API_URL}/api/contact/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAdminToken()}`
        }
      });
      if (response.ok) {
        fetchEnquiries(true);
      }
    } catch (err) {
      console.error('Error deleting enquiry:', err);
      alert('Failed to delete enquiry');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-[#7C4DFF] bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <RefreshCw className="w-8 h-8 animate-spin mb-4 text-[#D1D5DB]" />
        <p className="text-[#6B7280] font-medium">Loading Enquiries...</p>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_6px_20px_rgba(0,0,0,0.06)] overflow-hidden"
      >
        <div className="flex justify-between items-center p-6 border-b border-[#E5E7EB]">
          <div>
            <h2 className="text-xl font-bold text-[#111827] flex items-center gap-2">
              Contact Enquiries
              {refreshing && <RefreshCw className="w-4 h-4 animate-spin text-[#7C4DFF]" />}
            </h2>
            <p className="text-sm text-[#6B7280] mt-1">Manage all service requests and contact form submissions.</p>
          </div>
          <button
            onClick={() => fetchEnquiries(true)}
            className="text-[#6B7280] hover:text-[#7C4DFF] bg-[#F9FAFB] p-2.5 rounded-xl border border-[#E5E7EB] transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin text-[#7C4DFF]' : ''}`} />
          </button>
        </div>

        {error && (
          <div className="p-4 mx-6 mt-6 rounded-xl bg-red-50 text-red-600 text-sm font-medium border border-red-100">
            {error}
          </div>
        )}

        <div className="overflow-x-auto p-0">
          <table className={`w-full text-left border-collapse ${replyModal.isOpen ? 'pointer-events-none' : ''}`}>
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-[#4B5563] text-sm font-semibold uppercase tracking-wider">
                <th className="py-4 px-6 min-w-[200px]">Client Details</th>
                <th className="py-4 px-6 min-w-[200px]">Contact Info</th>
                <th className="py-4 px-6 min-w-[250px]">Enquiry Details</th>
                <th className="py-4 px-6">Status</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {enquiries.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-16 text-center text-[#6B7280] font-medium bg-[#F9FAFB]">
                    <MessageSquare className="w-12 h-12 mx-auto text-[#D1D5DB] mb-3" />
                    No enquiries found.
                  </td>
                </tr>
              ) : (
                enquiries.map(enquiry => (
                  <tr key={enquiry._id} className="border-b border-[#E5E7EB] hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="font-bold text-[#111827]">{enquiry.name}</div>
                      {enquiry.company && (
                        <div className="flex items-center gap-1.5 text-xs text-[#6B7280] mt-1">
                          <Briefcase className="w-3 h-3" /> {enquiry.company}
                        </div>
                      )}
                      <div className="text-[11px] text-gray-400 mt-1">
                        {new Date(enquiry.createdAt).toLocaleString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-[#6B7280]">
                      <div className="flex items-center gap-2 text-xs mb-1.5">
                        <Mail className="w-3 h-3" />
                        <a href={`mailto:${enquiry.email}`} className="hover:text-[#7C4DFF] transition-colors">{enquiry.email}</a>
                      </div>
                      {enquiry.phone && (
                        <div className="flex items-center gap-2 text-xs">
                          <Phone className="w-3 h-3" />
                          <a href={`tel:${enquiry.phone}`} className="hover:text-[#7C4DFF] transition-colors">{enquiry.phone}</a>
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="font-medium text-[#7C4DFF] mb-1">{enquiry.service}</div>
                      {enquiry.budget && (
                        <div className="text-xs font-semibold text-emerald-600 mb-1.5">Budget: {enquiry.budget}</div>
                      )}
                      <div className="text-xs text-[#4B5563] bg-white p-2 rounded border border-[#E5E7EB] max-h-20 overflow-y-auto" title={enquiry.message}>
                        {enquiry.message}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${enquiry.status === 'Responded' ? 'bg-[#D1FAE5] text-[#059669]' :
                        enquiry.status === 'Read' ? 'bg-[#FEF3C7] text-[#D97706]' :
                          'bg-blue-50 text-blue-600'
                        }`}>
                        {enquiry.status || 'New'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex justify-end gap-2">
                        {enquiry.status === 'New' && (
                          <button
                            onClick={() => handleStatusUpdate(enquiry._id, 'Read')}
                            className="p-2 rounded-lg bg-yellow-50 text-yellow-600 hover:bg-yellow-100 border border-yellow-200 transition-colors"
                            title="Mark as Read"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenReplyModal(enquiry)}
                          className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 transition-colors"
                          title="Reply via Email"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        {enquiry.status !== 'Responded' && (
                          <button
                            onClick={() => handleStatusUpdate(enquiry._id, 'Responded')}
                            className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 border border-green-200 transition-colors"
                            title="Mark as Responded Manually"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(enquiry._id)}
                          className="p-2 rounded-lg bg-white border border-[#FCA5A5] text-[#EF4444] hover:bg-red-50 transition-colors"
                          title="Delete Enquiry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Reply Modal */}
      {createPortal(
        <AnimatePresence>
          {replyModal.isOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative max-w-lg w-full bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-[#E5E7EB]"
              >
                {/* Modal Header */}
                <div className="w-full flex justify-between items-center p-5 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <h3 className="font-bold text-[#111827] text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#7C4DFF]" /> Reply to Enquiry
                  </h3>
                  <button
                    onClick={() => setReplyModal(prev => ({ ...prev, isOpen: false }))}
                    className="text-gray-500 hover:text-gray-700 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Modal Form */}
                <form onSubmit={handleSendReply} className="p-6 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#4B5563] block mb-1">Recipient Info</label>
                    <div className="flex gap-2">
                      <input type="text" disabled value={replyModal.name} className="w-1/2 h-[40px] px-3 rounded-lg bg-gray-100 border border-[#D1D5DB] text-[#4B5563] outline-none font-medium cursor-not-allowed" />
                      <input type="email" disabled value={replyModal.email} className="w-1/2 h-[40px] px-3 rounded-lg bg-gray-100 border border-[#D1D5DB] text-[#4B5563] outline-none font-medium cursor-not-allowed" />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#4B5563] block mb-1">Email Subject</label>
                    <input
                      type="text"
                      required
                      value={replyModal.subject}
                      onChange={e => setReplyModal(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full h-[40px] px-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#4B5563] block mb-1">Email Message</label>
                    <textarea
                      required
                      rows={8}
                      value={replyModal.message}
                      onChange={e => setReplyModal(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full p-3 rounded-lg bg-white border border-[#D1D5DB] text-[#1F2937] focus:border-[#7C4DFF] focus:ring-1 focus:ring-[#7C4DFF] outline-none transition-shadow text-sm resize-none"
                    />
                  </div>

                  <div className="flex gap-4 pt-2">
                    <button
                      type="button"
                      onClick={() => setReplyModal(prev => ({ ...prev, isOpen: false }))}
                      className="w-1/2 border border-[#D1D5DB] text-[#4B5563] hover:bg-gray-50 py-2.5 rounded-xl font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSendingReply}
                      className="w-1/2 bg-gradient-to-r from-[#7C4DFF] to-[#9B6DFF] hover:shadow-lg text-white py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isSendingReply ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Send Reply</>}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default AdminContactEnquiries;
